import { useMemo, useState } from "react";

import Box from "@mui/material/Box";
import type { GridColumnVisibilityModel } from "@mui/x-data-grid";
import { type GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import invariant from "ts-invariant";

import type { Patients } from "api/schemas/Patients";
import { useProfileContext } from "contexts/ProfileContext";
import { MUIDataGrid, useResizeColumns } from "shared/molecules/MUIDataGrid";
import { PatientStatus } from "shared/molecules/PatientStatus";
import { DataRegionTypes } from "types";
import { getAppDataRegion } from "utils/region";
import { getStorageValue, setStorageValue } from "utils/storage";
import { translateTreatment } from "utils/translations";

import { DateLabel } from "../DateLabel";
import { formatCoverage } from "../queries/fetchPatients";

import { Icons } from "./components";
import { PatientsToolbar } from "./PatientsToolbar";

const getInitialVisibilityModel = () => {
  const savedModel = getStorageValue("patient_list_visible_columns");
  return savedModel || {};
};

const paginationModel = { page: 0, pageSize: 50 };

interface Props {
  isLoading: boolean;
  patients: Patients[];
  totalPatients: number;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onFilterClick: () => void;
}

export const PatientsTable: React.FC<Props> = ({
  isLoading,
  patients,
  totalPatients,
  searchTerm,
  onSearchChange,
  onFilterClick,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { apiRef } = useResizeColumns();
  const region = getAppDataRegion();
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(getInitialVisibilityModel());
  const { profile } = useProfileContext();
  invariant(profile);

  const columns = useMemo((): GridColDef[] => {
    let columnsArray: GridColDef[] = [
      {
        field: "id",
        headerName: t("patients.id"),
        width: 82,
      },
      {
        field: "name",
        headerName: t("patients.name"),
        flex: 2,
        minWidth: 120,
        valueGetter: (_value, row) => `${row.first_name || ""} ${row.last_name || ""}`,
      },
      {
        field: "icons",
        headerName: t("patients.icons"),
        flex: 1,
        minWidth: 100,
        renderCell: params => <Icons params={params} />,
        disableColumnMenu: true,
        sortable: false,
      },
      {
        field: "ailment",
        headerName: t("patients.ailment"),
        flex: 1,
        minWidth: 120,
        valueGetter: (_value, row) =>
          translateTreatment({
            ailment: row.ailment,
            lateral_location: row.primary_joint?.lateral_location,
            location: row.primary_joint?.location,
          }),
      },
      {
        field: "week",
        headerName: t("patients.week"),
        flex: 1,
        maxWidth: 80,
      },
      {
        field: "status",
        headerName: t("patients.status"),
        flex: 1,
        minWidth: 100,
        renderCell: ({ row }) => (
          <PatientStatus
            patientStatuses={{
              deleted: row.deleted,
              immediate_referral_required: row?.us_insurance_profiles?.some(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (p: any) => p.immediate_referral_required
              ),
              pre_authorization_required: row?.us_insurance_profiles?.some(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (p: any) => p.pre_authorization_required
              ),
              status: row.status,
            }}
          />
        ),
      },
      {
        field: "last_activity_completed_at",
        headerName: t("patients.last_activity_completed_at"),
        flex: 1,
        minWidth: 120,
        renderCell: ({ row }) => <DateLabel date={row.last_activity_completed_at} />,
      },
      {
        field: "adherence",
        headerName: t("patients.adherence"),
        flex: 1,
        maxWidth: 80,
        renderCell: ({ row }) => <DisplayAdherence $adherence={row.adherence}>{row.adherence}%</DisplayAdherence>,
      },
    ];

    if (region === DataRegionTypes.US || region === DataRegionTypes.UK) {
      columnsArray.push({
        field: "coverage",
        flex: 1,
        minWidth: 135,
        headerName: (() => {
          switch (getAppDataRegion()) {
            case DataRegionTypes.US:
              return t("patients.header_info.coverage");
            case DataRegionTypes.UK:
              return t("form.payer_name");
            default:
              return "";
          }
        })(),
        valueGetter: (_value, row) => formatCoverage(t, row),
      });
    }

    if (region === DataRegionTypes.US) {
      columnsArray = columnsArray.concat([
        {
          field: "subscription",
          headerName: t("patients.header_info.subscription"),
          flex: 1,
          valueGetter: (_value, row) =>
            `${row.subscription?.status ?? ""} ${
              row.subscription?.is_free_trial ? `(${t("patients.header_info.subscription_free_trial")})` : ""
            }`,
        },
        {
          field: "address_state",
          headerName: t("patients.header_info.address_state"),
          flex: 1,
          valueGetter: (_value, row) => row.address_state?.code,
        },
      ]);
    }

    return columnsArray;
  }, []);

  const handleVisibilityChange = (newModel: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(newModel);
    setStorageValue("patient_list_visible_columns", newModel);
  };

  return (
    <Box sx={{ width: "100%", height: "100%", boxSizing: "border-box" }}>
      <PatientsToolbar
        apiRef={apiRef}
        patientCount={patients.length}
        totalPatients={totalPatients}
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        onFilterClick={onFilterClick}
      />
      <MUIDataGrid
        hideHeader
        apiRef={apiRef}
        columns={columns}
        rows={patients}
        getRowId={row => `${row.id}-${row.therapist_assignment_role}`}
        onCellClick={({ field, row }) => {
          if (field !== "icons") {
            navigate(`/patients/${row.id}`, { state: { pathname, text: "patients" } });
          }
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleVisibilityChange}
        loading={isLoading}
        initialState={{ pagination: { paginationModel } }}
        rowSelection={false}
        localeText={{ noRowsLabel: t("patients.no_patients") }}
        sx={{
          border: 0,
          cursor: "pointer",
          "& .MuiDataGrid-virtualScroller": {
            paddingBottom: "17px",
          },
        }}
      />
    </Box>
  );
};

const DisplayNumber = styled.div`
  margin: auto;
  color: ${props => props.theme.colors.greys.dark};
  font-weight: ${props => props.theme.fontWeight.regular};
`;

const DisplayAdherence = styled(DisplayNumber)<{ $adherence: number }>`
  color: ${({ $adherence, theme }) => $adherence < 60 && theme.colors.orange};
`;
